import { useCallback } from 'react';
import { getGenreService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  Genre,
  GenresQueryParams,
  GenreCreateRequest,
  GenreUpdateRequest,
} from '../types/api/genre';

export function useGenres() {
  const genreService = getGenreService();

  const fetchGenres: FetchDataFn<Genre> = useCallback(async (params: TableQueryParams): Promise<TableDataResponse<Genre>> => {
    const apiParams: GenresQueryParams = {
      page: params.page, // backend now 1-based
      size: params.pageSize,
    };
    if (params.search) apiParams.search = params.search;

    const response = await genreService.getPublicGenres(apiParams);
    return {
      data: response.items,
      pagination: {
        page: response.page,
        pageSize: response.size,
        total: response.totalItems,
        totalPages: response.totalPages,
        hasNext: response.hasNext,
        hasPrev: response.hasPrevious,
      },
    };
  }, [genreService]);

  return { fetchGenres };
}

export function useGenreActions() {
  const genreService = getGenreService();

  const createGenre = useCallback(
    async (payload: { data: GenreCreateRequest; iconFile?: File | Blob | null }): Promise<Genre> => {
      return genreService.createGenre(payload);
    },
    [genreService]
  );

  const updateGenre = useCallback(
    async (genreId: string, payload: { data: GenreUpdateRequest; iconFile?: File | Blob | null }): Promise<Genre> => {
      return genreService.updateGenre(genreId, payload);
    },
    [genreService]
  );

  const deleteGenre = useCallback(async (genreId: string): Promise<void> => {
    await genreService.deleteGenre(genreId);
  }, [genreService]);

  return { createGenre, updateGenre, deleteGenre };
}
